import { useCallback, useEffect, useState, type FormEvent } from "react";

import {
  api,
  type CalendarColor,
  type Label as LabelData,
  type TemplateType,
  type WishlistItem,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ErrorAlert } from "@/components/error-alert";
import { LabelChips } from "@/components/label-chips";
import { PageSkeleton } from "@/components/page-skeleton";
import { TypeChips } from "@/components/type-chips";
import { typeLabel } from "@/lib/domain";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function WishlistPage() {
  const [items, setItems] = useState<WishlistItem[] | null>(null);
  const [labels, setLabels] = useState<LabelData[]>([]);
  const [colors, setColors] = useState<CalendarColor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [wishlist, labelData] = await Promise.all([api.wishlist(), api.labels()]);
      setItems(wishlist.items);
      setLabels(labelData.labels);
      setError(null);
    } catch (err) {
      setError(messageOf(err, "could not load Wishlist"));
    }
  }, []);

  useEffect(() => {
    void load();
    (async () => {
      try {
        setColors((await api.colors()).colors);
      } catch {
        // Colours are decoration here; Labels still list by name.
      }
    })();
  }, [load]);

  if (error && !items) return <ErrorAlert message={error} />;
  if (!items) return <PageSkeleton />;

  return (
    <div className="grid gap-4">
      {error && <ErrorAlert message={error} />}
      <Card>
        <CardHeader>
          <CardTitle>Wishlist</CardTitle>
          <CardDescription>
            Rough ideas, just text. When one is ready, promote it into a
            Template by choosing its Type and at least one Label.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <AddIdeaForm
            onAdded={async () => {
              setNotice(null);
              await load();
            }}
          />
          <Separator />
          {notice && (
            <p className="text-sm text-muted-foreground" role="status">
              {notice}
            </p>
          )}
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing on the Wishlist. Add an idea above.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((item) => (
                <IdeaRow
                  key={item.id}
                  item={item}
                  labels={labels}
                  colors={colors}
                  onChanged={load}
                  onPromoted={async (title) => {
                    setNotice(`"${title}" is now a Template. Find it under Templates.`);
                    await load();
                  }}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AddIdeaForm({ onAdded }: { onAdded: () => Promise<void> }) {
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createWishlist({ title: title.trim() });
      setTitle("");
      await onAdded();
    } catch (err) {
      setError(messageOf(err, "could not add idea"));
    }
    setBusy(false);
  }

  return (
    <form className="flex flex-wrap items-end gap-2" onSubmit={(ev) => void handleSubmit(ev)}>
      <div className="grid min-w-56 flex-1 gap-1.5">
        <Label htmlFor="idea-title">Idea</Label>
        <Input
          id="idea-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Learn to play the piano"
          required
        />
      </div>
      <Button type="submit" disabled={busy}>
        Add to Wishlist
      </Button>
      {error && (
        <div className="basis-full">
          <ErrorAlert message={error} />
        </div>
      )}
    </form>
  );
}

function IdeaRow({
  item,
  labels,
  colors,
  onChanged,
  onPromoted,
}: {
  item: WishlistItem;
  labels: LabelData[];
  colors: CalendarColor[];
  onChanged: () => Promise<void>;
  onPromoted: (title: string) => Promise<void>;
}) {
  const [title, setTitle] = useState(item.title);
  const [promoting, setPromoting] = useState(false);
  const [type, setType] = useState<TemplateType | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = title !== item.title;

  async function run(action: () => Promise<unknown>, fallback: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(messageOf(err, fallback));
    }
    setBusy(false);
  }

  function toggle(id: string) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={`${item.title} title`}
          className="min-w-56 flex-1"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!dirty || busy}
            onClick={() =>
              void run(async () => {
                await api.updateWishlist(item.id, { title: title.trim() });
                await onChanged();
              }, "could not save")
            }
          >
            Save
          </Button>
          <Button
            size="sm"
            variant={promoting ? "secondary" : "default"}
            disabled={busy}
            onClick={() => {
              setPromoting((p) => !p);
              setError(null);
            }}
          >
            {promoting ? "Cancel" : "Promote"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.deleteWishlist(item.id);
                await onChanged();
              }, "could not delete")
            }
          >
            Delete
          </Button>
        </div>
      </div>

      {promoting && (
        <div className="mt-3 rounded-lg border border-border bg-secondary/40 p-3">
          <p className="text-sm font-medium">Promote &ldquo;{item.title}&rdquo; to a Template</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Choose what kind of Template it becomes and give it at least one
            Label. It starts with a 30-minute duration; edit the rest under
            Templates.
          </p>
          <p className="mt-3 mb-1.5 text-xs font-medium text-muted-foreground">Type</p>
          <TypeChips value={type} onChange={setType} />
          <p className="mt-3 mb-1.5 text-xs font-medium text-muted-foreground">Labels</p>
          {labels.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">
              You have no Labels yet. Create one in Settings first.
            </p>
          ) : (
            <LabelChips labels={labels} colors={colors} picked={picked} onToggle={toggle} />
          )}
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              disabled={busy || type === null || picked.length === 0}
              onClick={() => {
                if (type === null) return;
                void run(async () => {
                  const { template } = await api.promoteWishlist(item.id, type, picked);
                  await onPromoted(template.title);
                }, "could not promote");
              }}
            >
              Promote to Template
            </Button>
            <span className="text-sm text-muted-foreground">
              {type === null
                ? "Pick a Type"
                : picked.length === 0
                  ? `${typeLabel(type)}, no Label picked`
                  : `${typeLabel(type)}, ${picked.length} Label${picked.length === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </li>
  );
}
